import { ModelElementType } from "@/components/entities";

/** Overlay of local mutations the server hasn't echoed yet. `/action/models`
 *  responses replace the list wholesale, so a poll that started before a
 *  mutation lands after it carrying pre-mutation data — without this, a fresh
 *  model vanishes, a deleted one is resurrected, a rename reverts. Entries
 *  retire the moment a response echoes them; the grace window is only a
 *  backstop so a mutation the server silently ignored can't assert forever. */
export const PENDING_GRACE_MS = 30_000;

type ModelKey = keyof ModelElementType;
type ModelFields = Partial<ModelElementType>;

/** A model that locally exists (add) or doesn't (remove), pending the server
 *  agreeing. `row` is what to re-insert while an add is unconfirmed. */
export interface PendingLifecycle {
  kind: "add" | "remove";
  at: number;
  row: ModelElementType;
}

/** Optimistic field edits on one model. `previous` holds the pre-edit value of
 *  each key so a failed request rolls back exactly its own fields. */
export interface PendingPatch {
  at: number;
  fields: ModelFields;
  previous: ModelFields;
}

export type PendingLifecycleMap = Record<string, PendingLifecycle>;
export type PendingPatchMap = Record<string, PendingPatch>;

export interface ReconcileResult {
  models: ModelElementType[];
  lifecycle: PendingLifecycleMap;
  patches: PendingPatchMap;
}

const fresh = (at: number, nowMs: number): boolean =>
  nowMs - at <= PENDING_GRACE_MS;

const keysOf = (fields: ModelFields): ModelKey[] =>
  Object.keys(fields) as ModelKey[];

const copyKey = <K extends ModelKey>(
  target: ModelFields,
  key: K,
  source: ModelFields,
): void => {
  target[key] = source[key];
};

const without = <T>(map: Record<string, T>, id: string): Record<string, T> => {
  const { [id]: _dropped, ...rest } = map;
  return rest;
};

/** Apply the overlay to a server list: suppress pending removes, re-insert
 *  pending adds, re-apply unechoed patch fields — and retire every entry the
 *  response confirms (or that outlived the grace window). */
export const reconcile = (
  serverModels: ModelElementType[],
  lifecycle: PendingLifecycleMap,
  patches: PendingPatchMap,
  nowMs = Date.now(),
): ReconcileResult => {
  const served = new Set(serverModels.map((m) => m.model_id));

  const keptLifecycle: PendingLifecycleMap = {};
  for (const [id, entry] of Object.entries(lifecycle)) {
    if (!fresh(entry.at, nowMs)) continue;
    const confirmed = entry.kind === "add" ? served.has(id) : !served.has(id);
    if (!confirmed) keptLifecycle[id] = entry;
  }

  const pendingAdds = Object.values(keptLifecycle)
    .filter((e) => e.kind === "add")
    .map((e) => e.row);
  const overlaid = [
    ...serverModels.filter((m) => keptLifecycle[m.model_id]?.kind !== "remove"),
    ...pendingAdds,
  ];

  const keptPatches: PendingPatchMap = {};
  const models = overlaid.map((model) => {
    const patch = patches[model.model_id];
    if (!patch || !fresh(patch.at, nowMs)) return model;
    // A response echoing a field's patched value confirms that field; only
    // the still-unechoed remainder stays pending.
    const unechoed = keysOf(patch.fields).filter((key) =>
      // The server stamps its own pin date, so only its presence can echo.
      key === "starred_at"
        ? (model.starred_at == null) !== (patch.fields.starred_at == null)
        : model[key] !== patch.fields[key],
    );
    if (unechoed.length === 0) return model;
    const kept: PendingPatch = { at: patch.at, fields: {}, previous: {} };
    for (const key of unechoed) {
      copyKey(kept.fields, key, patch.fields);
      copyKey(kept.previous, key, patch.previous);
    }
    keptPatches[model.model_id] = kept;
    return { ...model, ...kept.fields };
  });

  return { models, lifecycle: keptLifecycle, patches: keptPatches };
};

/** Merge `fields` into the model's pending patch. `previous` keeps the FIRST
 *  seen value per key, so two in-flight edits to one model (star + rename)
 *  each roll back exactly their own fields. */
export const stagePatch = (
  patches: PendingPatchMap,
  id: string,
  fields: ModelFields,
  currentRow: ModelElementType,
  nowMs = Date.now(),
): PendingPatchMap => {
  const existing = patches[id];
  const previous: ModelFields = { ...existing?.previous };
  for (const key of keysOf(fields)) {
    if (!existing || !(key in existing.fields))
      copyKey(previous, key, currentRow);
  }
  return {
    ...patches,
    [id]: { at: nowMs, fields: { ...existing?.fields, ...fields }, previous },
  };
};

export interface PatchRollback {
  patches: PendingPatchMap;
  /** The pre-edit values to write back onto the model row. */
  restore: ModelFields;
}

/** Undo the given keys of a pending patch, leaving other in-flight fields. */
export const rollbackPatch = (
  patches: PendingPatchMap,
  id: string,
  keys: ModelKey[],
): PatchRollback => {
  const entry = patches[id];
  if (!entry) return { patches, restore: {} };

  const restore: ModelFields = {};
  const kept: PendingPatch = {
    at: entry.at,
    fields: { ...entry.fields },
    previous: { ...entry.previous },
  };
  for (const key of keys) {
    if (!(key in entry.fields)) continue;
    copyKey(restore, key, entry.previous);
    delete kept.fields[key];
    delete kept.previous[key];
  }

  const rest = without(patches, id);
  return {
    patches: keysOf(kept.fields).length > 0 ? { ...rest, [id]: kept } : rest,
    restore,
  };
};

export const rollbackLifecycle = (
  lifecycle: PendingLifecycleMap,
  id: string,
): PendingLifecycleMap => without(lifecycle, id);
